import jwt from "jsonwebtoken";
import { buildPasswordResetOtpEmail } from "./passwordResetOtpEmail";

/**
 * Org Registry service client.
 *
 * Every Org Registry call in this app authenticates the same way: an RS256 JWT
 * signed with the Org Registry service credentials (aud / iss / private key from
 * .env). Calls made on behalf of a signed-in user also carry that user's access
 * token in a `usertoken` claim; pre-login calls (login, forgot-password OTP) do not.
 *
 * This module centralises that so the forgot-password flow does not have to
 * repeat the jwt.sign boilerplate that Login/DashboardView/Groups each carry.
 */

const { REACT_APP_OrgRegistryApi, REACT_APP_OrgjwtAud, REACT_APP_OrgjwtIss, REACT_APP_OrgjwtPrivate } = process.env;

// Matches the expiry the existing Login screen uses.
const TOKEN_TTL_SECONDS = 1000;

// AppID the OTP service attributes the request to.
const OTP_APP_ID = "Control-Center";

// The reset endpoint takes a different AppID from the OTP endpoint - that is what the
// service spec says, not a copy/paste slip.
const RESET_APP_ID = "Report";

/**
 * Sign a service token for the Org Registry.
 * @param {string} [userToken] - a logged-in user's access token, when the endpoint needs one.
 */
export function getOrgRegistryToken(userToken) {
  const payload = {
    aud: REACT_APP_OrgjwtAud,
    exp: Math.floor(Date.now() / 1000) + TOKEN_TTL_SECONDS,
    iss: REACT_APP_OrgjwtIss,
  };

  if (userToken) {
    payload.usertoken = userToken;
  }

  const privateKey = REACT_APP_OrgjwtPrivate.replace(/\\n/g, "\n");

  return jwt.sign(payload, privateKey, { algorithm: "RS256" });
}

/**
 * Fetch wrapper for the Org Registry. Signs the request, calls it, and unwraps the
 * standard { Status, Result } envelope — throwing when the service reports a failure
 * so callers only deal with the happy path.
 *
 * @param {string} path - route without a leading slash, e.g. "v1/witmeg/otp/request"
 * @param {object} options
 * @param {string} [options.method="GET"]
 * @param {object} [options.body]
 * @param {string} [options.userToken]
 * @returns {Promise<any>} the `Result` value from the response envelope
 */
export async function orgRegistryRequest(path, { method = "GET", body, userToken } = {}) {
  const token = getOrgRegistryToken(userToken);

  // REACT_APP_OrgRegistryApi already ends in "/", so strip a leading slash off the route.
  const url = `${REACT_APP_OrgRegistryApi}${path.replace(/^\//, "")}`;

  const requestOptions = {
    method,
    headers: {
      "Access-Control-Allow-Origin": "*",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  if (body !== undefined) {
    requestOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, requestOptions);

  let data;
  try {
    data = await response.json();
  } catch (e) {
    throw new Error(`Org Registry returned an unreadable response (${response.status}).`);
  }

  if (!response.ok || data.Status === false) {
    // On failure the service puts the reason in `Result` — e.g. the OTP verify
    // endpoint returns { Status: false, Result: "OTP code does not match!" }.
    const reason = typeof data.Result === "string" ? data.Result : null;
    throw new Error(reason || data.Error || data.Message || `Org Registry request failed (${response.status}).`);
  }

  return data.Result;
}

/* -------------------------------------------------------------------------- */
/* Forgot password                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Step 1 of the forgot-password flow: ask the Org Registry to email an OTP.
 *
 * POST /v1/witmeg/otp/request
 * Response: { "Status": true, "Result": "6a7d37691bca49d2262983f4" }
 *
 * The returned Result is the OTP request id and is required by the verify call
 * in step 2, so the caller must hold on to it.
 *
 * @param {string} email
 * @param {object} [options]
 * @param {string} [options.name] - user's name for the email greeting, when known.
 * @returns {Promise<string>} the OTP request id
 */
export async function requestPasswordResetOtp(email, { name } = {}) {
  const body = {
    AppID: OTP_APP_ID,
    RequestMethod: "Email",
    OTPContent: buildPasswordResetOtpEmail({ name, email }),
    OTPPlainTextContent: "Here is your code",
    Subject: "Reset Password",
    Email: email,
    MobileNo: "",
  };

  return orgRegistryRequest("v1/witmeg/otp/request", { method: "POST", body });
}

/**
 * Step 2 of the forgot-password flow: check the code the user typed.
 *
 * POST /v1/witmeg/otp/verify
 * Body:     { "RequestID": "6a7d37691bca49d2262983f4", "OTP": "111111" }
 * Failure:  { "Status": false, "Result": "OTP code does not match!" }
 *
 * Resolves when the code is accepted; rejects with the service's message
 * ("OTP code does not match!", expired, etc.) when it is not.
 *
 * @param {string} requestId - the Result from requestPasswordResetOtp
 * @param {string} otp
 */
export async function verifyPasswordResetOtp(requestId, otp) {
  return orgRegistryRequest("v1/witmeg/otp/verify", {
    method: "POST",
    body: { RequestID: requestId, OTP: otp },
  });
}

/**
 * Step 3 of the forgot-password flow: set the new password.
 *
 * POST /v1/witmeg/user/email/reset/password
 * Body:    { RequestID, OTP, Email, Password, AppID }
 * Success: { "Status": true,  "Result": "Password had been reset" }
 * Failure: { "Status": false, "Result": "Password reset failed" }
 *
 * The request id and OTP are both re-sent here, so a failure invalidates the pair and
 * the caller has to send the user back to step 1 for a fresh code.
 *
 * @param {object} params
 * @param {string} params.requestId
 * @param {string} params.otp
 * @param {string} params.email
 * @param {string} params.password
 */
export async function resetPasswordWithOtp({ requestId, otp, email, password }) {
  return orgRegistryRequest("v1/witmeg/user/email/reset/password", {
    method: "POST",
    body: {
      RequestID: requestId,
      OTP: otp,
      Email: email,
      Password: password,
      AppID: RESET_APP_ID,
    },
  });
}
